import React, { useCallback, useEffect, useState } from 'react';
import PositionsTopBar from './RecruiterPositionsTopBar';
import RecruiterPositionsTabsComponent from './RecruiterPositionsTabsComponent';
import { MyPositionType, PositionStatus } from 'types';
import RecruiterPositionCard from './RecruiterPositionCard';
import RecruiterPositionDrawer from './RecruiterPositionDrawer';
import CloseRecruiterPositionModal from './CloseRecruiterPositionModal';
import CloseRecruiterPositionConfirmationModal from './CloseRecruiterPositionConfirmationModal';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from 'src/context/UserProvider';
import { TbSearch } from 'react-icons/tb';
import { Spinner, TextInput } from 'flowbite-react';
import classNames from 'classnames';
import FilterDrawer from './filterDrawer/FilterDrawer';
import PostionFiltersForm from './filterDrawer/PostionFiltersForm';
import NoRecruiterPositionsContainer from './NoRecruiterPositionsContainer';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { Loader } from 'ui';
import { debounce } from 'lodash';
import { useMutation } from 'react-query';
import { getMyRegisteredPositions } from 'src/apis/position';
import { revertPositionTitle } from 'shared-utils';
import InviteCollaborator from '../position/InviteCollaborator';
import dayjs from 'dayjs';
import UpdatePositionExpiryModal from '../position/UpdatePositionExpiryModal';
import UpdateApplicationExpiryModal from '../position/UpdateApplicationExpiryModal';
import UpdateAutoRecommendationExpiryModal from '../position/UpdateAutoRecommendationExpiryModal';
import ReopenPositionConfirmationModal from './ReopenPositionConfirmationModal';

interface Props {
	positionsData: MyPositionType[];
	handleCreatePosition: () => void;
	disableCreate: boolean;
	allActivePositionsCount?: number;
	allClosedPositionsCount?: number;
}

const RecruiterPositionsContainer = ({ positionsData, handleCreatePosition, disableCreate, allActivePositionsCount, allClosedPositionsCount }: Props) => {
	console.log('RecruiterPositionsContainer', positionsData);
	const navigate = useNavigate();
	const { companyId } = useParams();
	const { isAccountManagerLoggedIn, isRecruiterLoggedIn } = useUserContext();
	const { setFilterApplied, isFilterLoading, selectedCompany, setSelectedCompany } = useRecommendationsContext();
	const [isPositionDrawerOpen, setIsPositionDrawerOpen] = useState<boolean>(false);
	const [hasUnpulishedPositions, setHasUnpulishedPositions] = useState<boolean>(false);
	const [positionId, setPositionId] = useState<string>();
	const [tab, setTab] = useState<PositionStatus>(PositionStatus.OPEN);
	const [openAddCollaboratorModal, setOpenAddCollaboratorModal] = useState(false);
	const [filteredPositions, setFilteredPositions] = useState<MyPositionType[]>([]);
	const [openPositionUpdateExpiryModal, setOpenPositionUpdateExpiryModal] = useState(false);
	const [openApplicationUpdateExpiryModal, setOpenApplicationUpdateExpiryModal] = useState(false);
	const [openAutoRecommendationUpdateExpiryModal, setOpenAutoRecommendationUpdateExpiryModal] = useState(false);
	const [selectedPosition, setSelectedPosition] = useState<MyPositionType | null>(null);

	const [isCloseRecruiterPositionConfirmationModalOpen, setIsCloseRecruiterPositionConfirmationModalOpen] =
		useState<boolean>(false);
	const [isReopenPositionModalOpen, setIsReopenPositionModalOpen] = useState<boolean>(false);
	const [isCloseRecruiterPositionModal, setIsCloseRecruiterPositionModal] = useState<boolean>(false);

	const handleViewPosition = (positionId: string) => {
		setPositionId(positionId);
		setIsPositionDrawerOpen(true);
	};

	const getMyRegisteredPositionsFn = useMutation(['getMyRegisteredPositions', companyId], getMyRegisteredPositions, {
		onSuccess(data) {
			if (tab === PositionStatus.OPEN && data?.positions) {
				setFilteredPositions(data?.positions.filter(p => p.position.positionStatus === PositionStatus.OPEN) ?? []);
			} else if (tab === PositionStatus.CLOSED && data?.positions) {
				setFilteredPositions(data?.positions.filter(p => p.position.positionStatus === PositionStatus.CLOSED) ?? []);
			} else {
				setFilteredPositions([]);
			}
		},
	});

	const changeHandler = (search: string) => {
		search = revertPositionTitle(search.toLowerCase()?.trim());
		if (isRecruiterLoggedIn || companyId?.length) getMyRegisteredPositionsFn.mutate({ companyId, name: search });
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), [positionsData]);

	useEffect(() => {
		if (tab === PositionStatus.OPEN) {
			setFilteredPositions(positionsData.filter(p => p.position.positionStatus === PositionStatus.OPEN));
		} else if (tab === PositionStatus.CLOSED) {
			setFilteredPositions(positionsData.filter(p => p.position.positionStatus === PositionStatus.CLOSED));
		}
	}, [positionsData, tab]);

	useEffect(() => {
		if (positionsData.length > 0) {
			setSelectedCompany(prev => {
				return { ...prev, company: positionsData[0].position.company };
			});
			setHasUnpulishedPositions(
				!isAccountManagerLoggedIn &&
				!!positionsData?.find(
					p =>
						!p?.position?.isPublished &&
						p.position.positionStatus === PositionStatus.OPEN &&
						dayjs().isBefore(p?.position?.expiresAt),
				),
			);
		}
	}, [positionsData]);

	return (
		<>
			<PositionsTopBar
				companyName={
					selectedCompany?.company?.name ||
					(positionsData?.length > 0 ? positionsData[0]?.position?.company?.name : undefined)
				}
				handleCreatePosition={handleCreatePosition}
				disableCreate={disableCreate}
			/>

			<div className="mt-4">
				<div className={classNames('flex mb-[10px] gap-2', 'flex-col')}>
					<div className="w-full flex flex-wrap items-center justify-between gap-[10px]">
						{/* Top Tabs for recommended and connected */}
						<RecruiterPositionsTabsComponent
							tab={tab}
							setTab={setTab}
							activePositions={allActivePositionsCount}
							closedPositions={allClosedPositionsCount}
						/>

						<div className="w-full md:mt-0 md:max-w-[522px] flex items-center gap-[12px] justify-stretch">
							{/* Search Input */}
							<TextInput
								icon={() => <TbSearch color="#C7C9D9" />}
								className="w-full [&_input]:bg-white"
								placeholder="Search Position"
								name="search"
								rightIcon={() => (getMyRegisteredPositionsFn?.isLoading ? <Spinner /> : null)}
								onChange={e => {
									debouncedChangeHandler(e.target.value);
								}}
							/>

							{/* Filter */}
							<FilterDrawer
								filterDrawerHeader={
									<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4 md:border-b md:pb-4">
										Filter by
									</h1>
								}
								filterDrawerBody={(filter: any) => {
									return (
										<div className="px-3 py-4">
											<PostionFiltersForm
												companyId={companyId}
												onApply={() => {
													setFilterApplied(true);
													filter.setFilterApplied(true);
												}}
												onClear={() => {
													setFilterApplied(false);
													filter.setFilterApplied(false);
												}}
												onClose={() => {
													filter.setShowFilterDrawer(false);
												}}
											/>
										</div>
									);
								}}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex justify-center my-5">
				<div className="flex flex-wrap gap-7 sm:flex-col w-full sm:gap-4">
					{isFilterLoading ? (
						<div className="w-full h-full flex items-center justify-center">
							<Loader />
						</div>
					) : filteredPositions.length ? (
						filteredPositions.map((p, i) => (
							<div key={i} className="sm:w-full md:min-w-[352px] md:w-[calc(33.3%-19px)]">
								<RecruiterPositionCard
									positionObj={p}
									handleEditPosition={() => {
										navigate(
											isAccountManagerLoggedIn
												? `/edit-position/${companyId}/${p.position?._id}`
												: `/edit-position/${p.position?._id}`,
										);
									}}
									handleDuplicatePosition={
										!hasUnpulishedPositions
											? () => {
												localStorage.setItem('duplicatePosition', JSON.stringify(p));
												navigate(isAccountManagerLoggedIn ? `/create-position/${companyId}` : `/create-position`);
											}
											: undefined
									}
									handleClosePosition={() => {
										setPositionId(String(p.position?._id));
										setIsCloseRecruiterPositionConfirmationModalOpen(true);
									}}
									handleReopenPosition={() => {
										setPositionId(String(p.position?._id));
										setIsReopenPositionModalOpen(true);
									}}
									handleAddCollaborator={() => {
										setPositionId(String(p.position?._id));
										setOpenAddCollaboratorModal(true);
									}}
									handleUpdatePositionExpiry={() => {
										setPositionId(String(p.position?._id));
										setSelectedPosition(p);
										setOpenPositionUpdateExpiryModal(true);
									}}
									handleUpdateApplicationExpiry={() => {
										setPositionId(String(p.position?._id));
										setSelectedPosition(p);
										setOpenApplicationUpdateExpiryModal(true);
									}}
									handleUpdateAutoRecommendationExpiry={() => {
										setPositionId(String(p.position?._id));
										setSelectedPosition(p);
										setOpenAutoRecommendationUpdateExpiryModal(true);
									}}
									handleViewPosition={handleViewPosition}
								/>
							</div>
						))
					) : (
						<div className=" mt-4 w-full h-full flex flex-col justify-center items-center">
							<div className="min-h-[350px] flex flex-col items-center justify-center">
								<NoRecruiterPositionsContainer
									title={tab === PositionStatus.OPEN ? 'No active job postings' : 'No closed job postings!'}
									description=""
									showAction={false}
								/>
							</div>
						</div>
					)}
				</div>
			</div>

			<CloseRecruiterPositionConfirmationModal
				isOpen={isCloseRecruiterPositionConfirmationModalOpen}
				onClose={() => setIsCloseRecruiterPositionConfirmationModalOpen(false)}
				onConfirm={() => {
					setIsCloseRecruiterPositionConfirmationModalOpen(false);
					setIsCloseRecruiterPositionModal(true);
				}}
			/>
			<ReopenPositionConfirmationModal
				isOpen={isReopenPositionModalOpen}
				positionId={positionId || ''}
				onClose={() => setIsReopenPositionModalOpen(false)}
			/>
			<CloseRecruiterPositionModal
				positionId={positionId || ''}
				isOpen={isCloseRecruiterPositionModal}
				onClose={() => setIsCloseRecruiterPositionModal(false)}
			/>

			<InviteCollaborator
				positionId={positionId || ''}
				isOpen={openAddCollaboratorModal}
				onClose={() => setOpenAddCollaboratorModal(false)}
			/>

			<RecruiterPositionDrawer
				isPositionDrawerOpen={isPositionDrawerOpen}
				positionId={positionId || ''}
				setIsPositionDrawerOpen={setIsPositionDrawerOpen}
				closeDrawer={() => setIsPositionDrawerOpen(false)}
			/>

			<UpdatePositionExpiryModal
				positionId={positionId || ''}
				currentExpiryDate={selectedPosition?.position?.expiresAt || new Date()}
				isOpen={openPositionUpdateExpiryModal}
				onClose={() => {
					setOpenPositionUpdateExpiryModal(false);
					setSelectedPosition(null);
				}}
			/>
			<UpdateApplicationExpiryModal
				positionId={positionId || ''}
				currentExpiryDate={selectedPosition?.position?.applicationExpiryDate}
				isOpen={openApplicationUpdateExpiryModal}
				onClose={() => {
					setOpenApplicationUpdateExpiryModal(false);
					setSelectedPosition(null);
				}}
			/>
			<UpdateAutoRecommendationExpiryModal
				positionId={positionId || ''}
				currentExpiryDate={selectedPosition?.position?.autoRecommendationExpiryDate}
				isOpen={openAutoRecommendationUpdateExpiryModal}
				onClose={() => {
					setOpenAutoRecommendationUpdateExpiryModal(false);
					setSelectedPosition(null);
				}}
			/>
		</>
	);
};

export default RecruiterPositionsContainer;
